import { React } from 'Imports';
import { FormControl, Select } from 'MaterialUIComponents';
import { IDropdownItem } from '$Components/FilterComponents/VPDropdown';
import { Box, Button, MenuProps } from '@mui/material';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faCaretDown, faCaretUp } from '@fortawesome/pro-solid-svg-icons';
import { useEffect, useRef, useState } from 'react';
import DropdownSwitch from '$Components/FilterComponents/Filters/DropdownSwitch';
import DropdownSearchField from '$Components/FilterComponents/Filters/DropdownSearchField';
import DropdownMenuItem from '$Components/FilterComponents/Filters/DropdownMenuItem';
import { isEncompass } from '$Utilities/integrationPlatform';
import * as scssStyles from '$CSS/settings.scss';

const styles = require('$Components/FilterComponents/Filters/SelectBase.scss') as {
    dropdownLabel: string;
    dropdownLabelText: string;
    dropdownSelectedCount: string;
    hasSelectedElementsGeotab: string;
    hasSelectedElementsEncompass: string;
    noSelectedElementsGeotab: string;
    noSelectedElementsEncompass: string;
    selectIcon: string;
    noResultsMessage: string;
    scrollAreaGeotab: string;
    scrollAreaEncompass: string;
    actionButtonsContainer: string;
    closeButtonGeotab: string;
    applyButtonGeotab: string;
    closeButtonEncompass: string;
    applyButtonEncompass: string;
};

interface IDropdownLabelProps {
    labelText: string;
    selectedCount: number;
}

const DropdownLabel = (props: IDropdownLabelProps) => {
    const hasItemsSelected = props.selectedCount > 0;
    const noItemsClassName = isEncompass ? styles.noSelectedElementsEncompass : styles.noSelectedElementsGeotab;
    const hasItemsClassName = isEncompass ? styles.hasSelectedElementsEncompass : styles.hasSelectedElementsGeotab;

    return (
        <div className={styles.dropdownLabel}>
            <div className={styles.dropdownLabelText}>{props.labelText}</div>
            <div className={styles.dropdownSelectedCount}>
                <div className={hasItemsSelected ? hasItemsClassName : noItemsClassName}>{props.selectedCount}</div>
            </div>
        </div>
    );
};

interface IFilterBarMultiSelectProps {
    label: string;
    items: IDropdownItem[];
    disabled: boolean;
    searchable?: boolean;
    toggleable?: boolean;
    toggleText?: string;
    toggleStatus?: boolean;
    onToggleChange?: (checked: boolean) => void;
    applyFilter: (selectedItems: IDropdownItem[]) => void;
    selectedValues: any[];
    isMobileView: boolean;
    sortItemsByValue?: boolean;
}

const MultiSelect = (props: IFilterBarMultiSelectProps) => {
    const initialSelectedValuesRef = useRef<any[]>([]);

    const initialDropdownItemsRef = useRef<IDropdownItem[]>(props.items || []);

    const initialSelectedItems = (props.selectedValues || [])
        .map((value) => props.items.find((i) => i.value === value))
        .filter((i): i is IDropdownItem => Boolean(i));

    const initialToggleStatusRef = useRef<boolean>(false);
    const [selectedItems, setSelectedItems] = useState<IDropdownItem[]>(initialSelectedItems);
    const [isOpen, setIsOpen] = useState(false);
    const [dropdownHasFocus, setDropdownHasFocus] = useState(false);
    const [searchFilter, setSearchFilter] = useState('');
    const [includeInactive, setIncludeInactive] = useState(false);
    const [selectionChanged, setSelectionChanged] = useState(false);
    const [wiggle, setWiggle] = useState(false);
    const [dropdownItems, setDropdownItems] = useState<IDropdownItem[]>([]);
    const [filteredDropdownItems, setFilteredDropdownItems] = useState<IDropdownItem[]>([]);

    const isFiltering = searchFilter.length > 0;

    const dropdownMenuProps: Partial<MenuProps> = {
        transformOrigin: {
            horizontal: props.isMobileView ? 'right' : 'left',
            vertical: 'top',
        },
        anchorOrigin: {
            horizontal: props.isMobileView ? 'right' : 'left',
            vertical: 'bottom',
        },
        disablePortal: true,
        slotProps: {
            root: {
                sx: {
                    width: 'auto',
                },
            },
            paper: {
                sx: {
                    overflowY: 'hidden !important',
                    maxHeight: 400,
                    width: 'auto',
                    minWidth: 'max-content',
                },
                className: wiggle ? 'vp-wiggle' : '',
            },
        },
    };

    useEffect(() => {
        let newSelectedItems: IDropdownItem[] = [];
        if (initialDropdownItemsRef.current.length !== props.items.length) {
            const initialSelectedItems = (props.selectedValues || [])
                .map((value) => props.items.find((i) => i.value === value))
                .filter((i): i is IDropdownItem => Boolean(i));
            newSelectedItems = initialSelectedItems;
        } else {
            const itemValues = props.items.map((i) => i?.value);
            newSelectedItems = selectedItems.filter((selectedItem) => selectedItem && itemValues.includes(selectedItem.value));
        }

        setSelectedItems(newSelectedItems);
        setDropdownItems(props.items);
    }, [props.items, props.selectedValues]);

    useEffect(() => {
        setFilteredDropdownItems(
            props.searchable
                ? dropdownItems.filter(
                      (item) =>
                          item.label.toLowerCase().includes(searchFilter.toLowerCase()) ||
                          item.secondaryLabel?.toLowerCase().includes(searchFilter.toLowerCase()) ||
                          item.extraSearchTerms?.some((term) => term.toLowerCase().includes(searchFilter.toLowerCase())),
                  )
                : dropdownItems,
        );
    }, [searchFilter, dropdownItems]);

    useEffect(() => {
        const initialValues = (
            initialSelectedValuesRef.current.length ? initialSelectedValuesRef.current : initialSelectedItems.map((item) => item.value)
        )
            .slice()
            .sort();
        const currentValues = selectedItems
            .map((item) => item.value)
            .slice()
            .sort();
        const valuesChanged =
            initialValues.length !== currentValues.length || !initialValues.every((value, index) => value === currentValues[index]);

        if (valuesChanged) {
            setSelectionChanged(true);
            return;
        }

        const includeInactiveChanged = props.toggleable ? includeInactive !== (initialToggleStatusRef.current ?? false) : false;
        if (includeInactiveChanged && (selectedItems || []).length === 0) {
            setSelectionChanged(true);
            return;
        }
        setSelectionChanged(false);
    }, [selectedItems, includeInactive]);

    const handleSearchFilterChange = (value: string) => {
        setSearchFilter(value);
    };

    const handleOpen = () => {
        setIsOpen(true);
        setSearchFilter('');
        setDropdownHasFocus(true);

        initialSelectedValuesRef.current = (selectedItems || [])
            .map((i) => i?.value)
            .filter((v): v is any => v !== undefined)
            .slice()
            .sort();

        initialDropdownItemsRef.current = (props.items || []).slice();

        if (props.toggleable) {
            initialToggleStatusRef.current = props.toggleStatus || false;
            setIncludeInactive(props.toggleStatus || false);
        }
    };

    const clearStates = () => {
        setIsOpen(false);
        setSearchFilter('');
        setDropdownHasFocus(false);
    };

    const handleClose = () => {
        clearStates();

        const restoredValues = initialSelectedValuesRef.current || [];
        const restoredItems = restoredValues
            .map((val) => {
                return initialDropdownItemsRef.current.find((i) => i.value === val) || props.items.find((i) => i.value === val);
            })
            .filter((i): i is IDropdownItem => Boolean(i));

        setSelectedItems(restoredItems);

        if (props.toggleable) {
            props.onToggleChange && props.onToggleChange(initialToggleStatusRef.current);
            setIncludeInactive(initialToggleStatusRef.current);
        }
    };

    const handleApply = () => {
        clearStates();
        props.applyFilter && props.applyFilter(selectedItems);

        initialSelectedValuesRef.current = (selectedItems || [])
            .map((i) => i?.value)
            .filter((v): v is any => v !== undefined)
            .slice()
            .sort();

        if (props.toggleable) {
            initialToggleStatusRef.current = includeInactive;
            props.onToggleChange && props.onToggleChange(includeInactive);
        }

        setSelectionChanged(false);
    };

    const handleMenuItemClick = (item: IDropdownItem) => {
        if (selectedItems.map((i) => i.value).includes(item.value)) {
            const newSelectedItems = selectedItems.filter((selectedItem) => selectedItem.value !== item.value);
            setSelectedItems(newSelectedItems);
        } else {
            const newSelectedItems = [...selectedItems, item];
            setSelectedItems(newSelectedItems);
        }
    };

    const handleSelectDeselectAll = () => {
        if (selectedItems.length < dropdownItems.length) {
            setSelectedItems(dropdownItems);
        } else {
            setSelectedItems([]);
        }
        return;
    };

    const handleToggleInactive = () => {
        const newValue = !includeInactive;
        setIncludeInactive(newValue);

        if (props.toggleable) {
            props.onToggleChange && props.onToggleChange(newValue);
        }
    };

    const handleClickAway = () => {
        if (isOpen) {
            if (selectionChanged) {
                setWiggle(true);
                setTimeout(() => setWiggle(false), 300);
            } else {
                handleClose();
            }
        }
    };

    return (
        <FormControl sx={{ width: props.isMobileView ? '100%' : 'max-content' }} focused={dropdownHasFocus}>
            <Select
                labelId={props.label + '-select-label'}
                id={props.label + '-select'}
                multiple
                inputProps={{ 'aria-label': 'Without label', sx: { padding: '0 !important' } }}
                displayEmpty
                value={[]}
                disabled={false}
                MenuProps={dropdownMenuProps}
                renderValue={() => <DropdownLabel labelText={props.label} selectedCount={selectedItems.length || 0} />}
                IconComponent={() => (
                    <Box className={styles.selectIcon} onClick={isOpen ? handleClose : handleOpen}>
                        <FontAwesomeIcon icon={isOpen ? faCaretUp : faCaretDown} color={scssStyles.videoEventFilterSelectIconColor} />
                    </Box>
                )}
                open={isOpen}
                onClose={() => handleClickAway()}
                onOpen={handleOpen}
                autoFocus={false}
                sx={{
                    cursor: 'pointer',
                    height: isEncompass ? '32px' : '40px',
                    '&:focus': { backgroundColor: 'transparent' },
                    fieldset: {
                        border: dropdownHasFocus
                            ? isEncompass
                                ? '1px solid #0D7AC5 !important'
                                : '1px solid #25477B !important'
                            : isEncompass
                              ? '1px solid #C4CBD4 !important'
                              : '1px solid #C4CBD7 !important',
                    },
                }}
            >
                {props.searchable && (
                    <DropdownSearchField
                        onChange={handleSearchFilterChange}
                        searchFilter={searchFilter}
                        onSearchFieldFocus={() => setDropdownHasFocus(false)}
                        onSearchFieldFocusLost={() => setDropdownHasFocus(true)}
                    />
                )}
                {props.toggleable && !isFiltering && (
                    <DropdownSwitch toggleText={props.toggleText || ''} onChange={handleToggleInactive} checked={includeInactive} />
                )}
                <Box className={isEncompass ? styles.scrollAreaEncompass : styles.scrollAreaGeotab}>
                    {!isFiltering && (
                        <DropdownMenuItem
                            value={'select-deselect-all'}
                            checked={selectedItems.length != 0 && selectedItems.length === dropdownItems.length}
                            onChange={handleSelectDeselectAll}
                            label={'Select/Deselect All'}
                        />
                    )}
                    {(filteredDropdownItems || [])
                        .sort((a, b) =>
                            props.sortItemsByValue ? a.value - b.value : a.label.toLowerCase().localeCompare(b.label.toLowerCase()),
                        )
                        .map((item, index) => (
                            <DropdownMenuItem
                                key={index}
                                value={item.value}
                                checked={selectedItems.map((i) => i.value).includes(item.value)}
                                label={item.label}
                                secondaryLabel={item.secondaryLabel}
                                onChange={() => handleMenuItemClick(item)}
                                searchFilter={searchFilter}
                            />
                        ))}
                </Box>
                {isFiltering && filteredDropdownItems.length === 0 && <div className={styles.noResultsMessage}>No results found</div>}
                <Box className={styles.actionButtonsContainer}>
                    <Button
                        variant="outlined"
                        size="small"
                        disableRipple
                        className={isEncompass ? styles.closeButtonEncompass : styles.closeButtonGeotab}
                        onClick={handleClose}
                    >
                        Close
                    </Button>
                    <Button
                        variant="contained"
                        disableElevation
                        disableRipple
                        size="small"
                        className={isEncompass ? styles.applyButtonEncompass : styles.applyButtonGeotab}
                        onClick={handleApply}
                        disabled={!selectionChanged}
                    >
                        Apply
                    </Button>
                </Box>
            </Select>
        </FormControl>
    );
};

export default MultiSelect;
